import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { JotaiProvider, Watchable } from '@cotera/client/v0/actions/framework';
import { cn } from '@cotera/client/v0/lib/utils';
import { DropdownMenuItem } from '@cotera/client/v0/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@cotera/client/v0/components/ui/dialog';
import { Input } from '@cotera/client/v0/components/ui/input';
import { DataGrid } from '../data-grid';
import { createDataGridViewModel } from '../view-model';
import type {
  DataGridCellEdit,
  DataGridColumn,
  DataGridFooterProps,
  DataGridHeaderContext,
  DataGridRowNumberContext,
  DataGridTopBarProps,
} from '../types';
import type { MarkdownTableData, MarkdownTableEditHandlers } from './types';

export type MarkdownTableGridProps = {
  data: MarkdownTableData;
  /** Supplying handlers turns editing on; omitting them renders read-only. */
  edit?: MarkdownTableEditHandlers;
  className?: string;
  /** Height cap before the grid body scrolls. */
  maxHeight?: number;
  /**
   * Offer the expand-cell button and modal, so a long value clipped by a cell
   * can be opened full-size. Off by default; see `DataGridCellDetailEnabledContext`.
   */
  enableCellDetail?: boolean;
};

const ROW_HEIGHT = 36;
const HEADER_HEIGHT = 40;
const DEFAULT_COLUMN_WIDTH = 200;
const DEFAULT_MAX_HEIGHT = 420;
// Room for the box's borders so the last row is never shaved by a hairline.
const BORDER_ALLOWANCE = 2;
const COLUMN_ID_PREFIX = 'col-';
const ROW_ID_PREFIX = 'row-';

/** A body row, tagged with its index so edits can be routed back to the source. */
type MarkdownGridRow = {
  id: string;
  index: number;
  cells: MarkdownTableData['rows'][number];
};

type RenameState = { columnIndex: number; value: string };

const getRowId = (row: MarkdownGridRow): string => row.id;

const parseRowIndex = (rowId: string | number): number | null => {
  const asString = String(rowId);
  if (!asString.startsWith(ROW_ID_PREFIX)) {
    return null;
  }
  const parsed = Number(asString.slice(ROW_ID_PREFIX.length));
  return Number.isInteger(parsed) ? parsed : null;
};

const parseColumnIndex = (columnId: string): number | null => {
  if (!columnId.startsWith(COLUMN_ID_PREFIX)) {
    return null;
  }
  const parsed = Number(columnId.slice(COLUMN_ID_PREFIX.length));
  return Number.isInteger(parsed) ? parsed : null;
};

/** Plain-text cell body; the rich `content` renderer is preferred when present. */
const CellText: React.FC<{ text: string }> = ({ text }) => (
  <span className="truncate">{text}</span>
);

/** Row-number gutter content: the index, swapped for a delete control on hover. */
const MarkdownRowNumber: React.FC<{
  rowIndex: number;
  onDelete: () => void;
}> = ({ rowIndex, onDelete }) => (
  <span className="relative flex w-full items-center justify-end">
    <span className="tabular-nums group-hover/gutter:opacity-0">
      {rowIndex + 1}
    </span>
    <button
      type="button"
      aria-label="Delete row"
      className="pointer-events-none absolute inset-y-0 right-0 flex items-center justify-center rounded p-0.5 text-muted-foreground opacity-0 hover:text-foreground group-hover/gutter:pointer-events-auto group-hover/gutter:opacity-100"
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.stopPropagation();
        onDelete();
      }}
    >
      <X className="h-3.5 w-3.5" />
    </button>
  </span>
);

const EditToolbar: React.FC<{
  onAddRow: () => void;
  onAddColumn: () => void;
}> = ({ onAddRow, onAddColumn }) => (
  <div className="flex items-center gap-2">
    <button
      type="button"
      className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
      onClick={onAddRow}
    >
      <Plus className="h-3 w-3" /> Row
    </button>
    <button
      type="button"
      className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
      onClick={onAddColumn}
    >
      <Plus className="h-3 w-3" /> Column
    </button>
  </div>
);

/** Rename dialog opened from a column's "Rename column…" menu item. */
const RenameColumnDialog: React.FC<{
  state: RenameState | null;
  onClose: () => void;
  onSubmit: (columnIndex: number, value: string) => void;
}> = ({ state, onClose, onSubmit }) => {
  const [value, setValue] = useState('');

  useEffect(() => {
    setValue(state?.value ?? '');
  }, [state]);

  return (
    <Dialog
      open={state !== null}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Rename column</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (state !== null) {
              onSubmit(state.columnIndex, value);
            }
          }}
        >
          <Input
            autoFocus
            aria-label="Column name"
            placeholder="Column name"
            value={value}
            onChange={(event) => setValue(event.target.value)}
          />
          <DialogFooter className="mt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rounded bg-primary px-3 py-1.5 text-sm text-primary-foreground hover:bg-primary/90"
            >
              Save
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const EmptyTopBar: React.FC<DataGridTopBarProps<MarkdownGridRow>> = () => null;
const EmptyFooter: React.FC<DataGridFooterProps<MarkdownGridRow>> = () => null;

const buildColumns = (
  data: MarkdownTableData,
  editable: boolean
): DataGridColumn<MarkdownGridRow>[] =>
  data.headers.map(
    (header, columnIndex): DataGridColumn<MarkdownGridRow> => ({
      id: `${COLUMN_ID_PREFIX}${columnIndex}`,
      header: header.content ?? header.text,
      // `content` is rich markup, so the expand modal titles off the source text.
      headerText: header.text,
      type: 'text',
      editorKind: 'text',
      width: DEFAULT_COLUMN_WIDTH,
      minWidth: 80,
      sortable: true,
      filterable: false,
      editable,
      getValue: (row) => row.cells[columnIndex]?.text ?? '',
      renderCell: (context) => {
        const cell = context.row.cells[columnIndex];
        if (cell === undefined) {
          return null;
        }
        return cell.content ?? <CellText text={cell.text} />;
      },
    })
  );

/**
 * Renders a markdown table through the {@link DataGrid}. Read-only by default;
 * pass `edit` to make cells, headers, and the row/column set editable. Keeps the
 * grid's own header (sort, pin, resize) and hangs rename/remove off each
 * column's menu. Sizes itself to its row count (capped by `maxHeight`) since the
 * grid otherwise fills its container, and carries its own `JotaiProvider` so it
 * works inside a ProseMirror node view where app context is not in scope.
 */
export const MarkdownTableGrid: React.FC<MarkdownTableGridProps> = ({
  data,
  edit,
  className,
  maxHeight = DEFAULT_MAX_HEIGHT,
  enableCellDetail = false,
}) => {
  const editable = edit !== undefined;
  const [renaming, setRenaming] = useState<RenameState | null>(null);

  const columns = useMemo(() => buildColumns(data, editable), [data, editable]);
  const gridRows = useMemo<MarkdownGridRow[]>(
    () =>
      data.rows.map((cells, index) => ({
        id: `${ROW_ID_PREFIX}${index}`,
        index,
        cells,
      })),
    [data.rows]
  );

  const rowsWatchable = useMemo(
    () => Watchable.fromValue<MarkdownGridRow[]>(gridRows),
    // Created once; the effect below keeps its value in sync.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const viewModel = useMemo(
    () =>
      createDataGridViewModel<MarkdownGridRow>({
        columns,
        rowHeight: ROW_HEIGHT,
        headerHeight: HEADER_HEIGHT,
        // No stats, so the header never grows when its expand toggle is hit.
        expandedHeaderHeight: HEADER_HEIGHT,
      }),
    // Created once; columns are pushed through the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    rowsWatchable.set(gridRows);
  }, [gridRows, rowsWatchable]);
  useEffect(() => {
    viewModel.columns.set(columns);
  }, [columns, viewModel]);

  const handleCellEdit = useCallback(
    (cellEdit: DataGridCellEdit) => {
      if (edit === undefined) {
        return;
      }
      const columnIndex = parseColumnIndex(cellEdit.columnId);
      const rowIndex = parseRowIndex(cellEdit.rowId);
      if (columnIndex === null || rowIndex === null) {
        return;
      }
      const nextValue = String(cellEdit.value ?? '');
      if (data.rows[rowIndex]?.[columnIndex]?.text === nextValue) {
        return;
      }
      edit.onCellChange(rowIndex, columnIndex, nextValue);
    },
    [edit, data.rows]
  );

  const renderRowNumber = useCallback(
    (context: DataGridRowNumberContext<MarkdownGridRow>) => {
      if (edit === undefined) {
        return context.rowIndex + 1;
      }
      return (
        <MarkdownRowNumber
          rowIndex={context.rowIndex}
          onDelete={() => edit.onRemoveRow(context.row.index)}
        />
      );
    },
    [edit]
  );

  const renderColumnMenuItems = useCallback(
    (context: DataGridHeaderContext<MarkdownGridRow>) => {
      if (edit === undefined) {
        return null;
      }
      const columnIndex = parseColumnIndex(context.column.id);
      if (columnIndex === null) {
        return null;
      }
      return (
        <>
          <DropdownMenuItem
            onSelect={() =>
              setRenaming({
                columnIndex,
                value: data.headers[columnIndex]?.text ?? '',
              })
            }
          >
            Rename column…
          </DropdownMenuItem>
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onSelect={() => edit.onRemoveColumn(columnIndex)}
          >
            Remove column
          </DropdownMenuItem>
        </>
      );
    },
    [edit, data.headers]
  );

  const height = Math.min(
    maxHeight,
    HEADER_HEIGHT + Math.max(gridRows.length, 1) * ROW_HEIGHT + BORDER_ALLOWANCE
  );

  return (
    <JotaiProvider>
      {/* `isolate` keeps the grid's internal sticky header (z-30) in its own
          stacking context so it can't paint over a sibling artifact card/popover
          sitting beside it in the chat transcript. */}
      <div className={cn('isolate my-2 flex flex-col gap-1.5', className)}>
        <div
          className="overflow-hidden rounded-md border border-border"
          style={{ height }}
        >
          <DataGrid
            rows={rowsWatchable}
            getRowId={getRowId}
            viewModel={viewModel}
            TopBarComponent={EmptyTopBar}
            FooterComponent={EmptyFooter}
            showStatsToggle={false}
            enableCellDetail={enableCellDetail}
            {...(editable && {
              onCellEdit: handleCellEdit,
              autoSaveEdits: true,
              renderRowNumber,
              renderColumnMenuItems,
            })}
          />
        </div>
        {editable ? (
          <EditToolbar
            onAddRow={edit.onAddRow}
            onAddColumn={edit.onAddColumn}
          />
        ) : null}
      </div>
      {editable ? (
        <RenameColumnDialog
          state={renaming}
          onClose={() => setRenaming(null)}
          onSubmit={(columnIndex, value) => {
            edit.onHeaderChange(columnIndex, value);
            setRenaming(null);
          }}
        />
      ) : null}
    </JotaiProvider>
  );
};
